/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface ParsedScriptTag {
    attrs: Record<string, string>;
    content: string;
}

const SCRIPT_TAG_RE = /<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi;
const ATTR_RE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;

/**
 * Splits a raw HTML snippet (e.g. a `omnitureHeaderInclude`/`omnitureFooterInclude`
 * site preference) into its individual top-level `<script>` tags, so each can be
 * rendered as its own sibling `<script>` element instead of being nested inside a
 * single wrapping `<script>` — nesting breaks the outer tag the moment the raw
 * snippet contains its own `</script>` (the browser's tokenizer closes the
 * *outer* script at that point, since `<script>` content is parsed as raw text,
 * not as nested markup).
 *
 * Only `<script>` tags are recognized — this is sufficient for real-world
 * Omniture/Adobe Launch includes (one or more `<script>` tags, e.g. an external
 * Launch library `<script src>` plus an inline `_satellite.pageBottom()` call).
 * Any other markup or text between/around the script tags (comments,
 * `<noscript>` fallbacks, whitespace) is not preserved. Attribute values are
 * copied verbatim; script bodies are trimmed of surrounding whitespace only —
 * no other processing.
 */
export function parseScriptTags(raw: string): ParsedScriptTag[] {
    const tags: ParsedScriptTag[] = [];
    let match: RegExpExecArray | null;

    SCRIPT_TAG_RE.lastIndex = 0;
    while ((match = SCRIPT_TAG_RE.exec(raw))) {
        const [, attrString, content] = match;
        const attrs: Record<string, string> = {};

        ATTR_RE.lastIndex = 0;
        let attrMatch: RegExpExecArray | null;
        while ((attrMatch = ATTR_RE.exec(attrString))) {
            const [, name, dq, sq, bare] = attrMatch;
            // Value-less/boolean attributes (e.g. `async`) get the attribute name
            // itself as a truthy placeholder value — React's boolean-attribute
            // handling treats an empty string as `false` and strips the
            // attribute entirely, which would silently drop `async`/`defer`.
            attrs[name] = dq ?? sq ?? bare ?? name;
        }

        tags.push({ attrs, content: content.trim() });
    }

    return tags;
}
